import { type CustomFieldProps, useField } from 'informed';
import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ThemeMode } from '@/helpers/enums';
import type { ValidatorMessage } from '@/helpers/validators';
import type { CustomFormFieldProps } from './contracts';
import { SelectBlock } from './primitives/SelectBlock';
import type { SelectBlockProps } from './primitives/types';
import { getErrorTranslation } from './utils';

function asSelectField<P extends SelectBlockProps, V>(
  Component: React.ComponentType<P>
): React.ComponentType<CustomFieldProps<CustomFormFieldProps, V> & P> {
  return ({
    translateError = true,
    onSelectOption,
    onClose,
    error: propsError,
    ...fieldProps
  }) => {
    const { t } = useTranslation();

    const { fieldState, fieldApi, render, ref, userProps } = useField<P, V>(
      fieldProps
    );
    const { setValue, setTouched } = fieldApi;
    const { value, error } = fieldState;

    const handleSelect = useCallback(
      (value: V) => {
        setValue(value);
        setTouched(true);

        if (onSelectOption) onSelectOption(value as any);
      },
      [setValue, setTouched, onSelectOption]
    );

    const handleClose = useCallback(() => {
      setTouched(true);

      if (onClose) onClose();
    }, [setTouched, onClose]);

    function getError() {
      if (propsError !== null) return propsError;

      if (!!error && translateError) {
        return getErrorTranslation(t, error as ValidatorMessage);
      }
      return error;
    }

    return render(
      <Component
        {...userProps}
        {...fieldState}
        {...fieldApi}
        error={getError()}
        mode={ThemeMode.Dark}
        onClose={handleClose}
        onSelectOption={handleSelect}
        ref={ref}
        selected={value}
      />
    );
  };
}

export const Select = asSelectField(SelectBlock);
